// src/components/cards/ClientAccountCard.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import {
  Client,
  invoiceAPI,
  InvoiceSummary,
  AccountSummary,
  LedgerEntry,
} from '../../services/api';
import AddPaymentModal from '../modals/AddPaymentModal';
import { AccountActionButton } from '../common/Buttons';
import LedgerList from '../lists/AccountsList/LedgerList';
import InvoiceList from '../lists/AccountsList/InvoiceList';
import { FloatingAccessButton } from '../common/FloatingAccessButton';
import { CardFooterContainer, CardFooter } from './shared/CardElements';
import { formatCurrency } from '../lists/AccountsList/accountTypes';
import AccountsPageSubMenu from '../menus/AccountsPageSubMenu';

interface ClientAccountCardProps {
  client: Client;
}

const TabPanel: React.FC<{
  children: React.ReactNode;
  value: number;
  index: number;
}> = ({ children, value, index }) => (
  <div hidden={value !== index} role="tabpanel">
    {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
  </div>
);

const AccountBalanceRow = styled(Box)(() => ({
  padding: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  '@media (max-width: 600px)': {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const ACCOUNT_TABS = [
  { label: 'Ledger', minWidth: '130px' },
  { label: 'Invoices', minWidth: '140px' },
];

const ClientAccountCard: React.FC<ClientAccountCardProps> = ({ client }) => {
  const theme = useTheme();
  const location = useLocation();

  const initialTab =
    typeof (location.state as any)?.accountTab === 'number'
      ? (location.state as any).accountTab
      : 0;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [accountSummary, setAccountSummary] = useState<AccountSummary | null>(
    null
  );
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [preselectedInvoiceId, setPreselectedInvoiceId] = useState<
    number | undefined
  >();

  const loadAccountData = useCallback(async () => {
    setLoading(true);
    try {
      const [summary, invoiceList] = await Promise.all([
        invoiceAPI.getAccountSummary(client.id),
        invoiceAPI.getByClient(client.id),
      ]);
      setAccountSummary(summary);
      setInvoices(invoiceList);
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  }, [client.id]);

  useEffect(() => {
    loadAccountData();
  }, [loadAccountData]);

  const handleDownloadPdf = async (invoiceId: number) => {
    try {
      const blob = await invoiceAPI.downloadPdf(invoiceId);
      const inv = invoices.find((i) => i.id === invoiceId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = inv
        ? `Invoice_${inv.invoiceNumber}.pdf`
        : `Invoice_${invoiceId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Silent
    }
  };

  const handlePayInvoice = (invoiceId: number) => {
    setPreselectedInvoiceId(invoiceId);
    setPaymentModalOpen(true);
  };

  const ledger: LedgerEntry[] = accountSummary?.entries ?? [];
  const currentBalance = accountSummary?.outstandingBalance ?? 0;
  const isCredit = currentBalance < 0;

  const balanceDisplay =
    currentBalance === 0
      ? 'R 0.00'
      : isCredit
        ? `${formatCurrency(Math.abs(currentBalance))} CR`
        : `${formatCurrency(currentBalance)} DUE`;

  const cardFooterProps = {
    borderTop: `3px solid ${theme.palette.grey[900]}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[600],
  };

  const footerTextSx = {
    p: '1rem 1.5rem',
    fontSize: '2rem',
    fontWeight: 600,
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[400]
        : theme.palette.grey[0],
  };

  const handleAddPaymentClick = () => {
    setPreselectedInvoiceId(undefined);
    setPaymentModalOpen(true);
  };

  const addPaymentFab = (
    <FloatingAccessButton
      icon={<AddIcon />}
      hoverIcon={<AddIcon />}
      onClick={handleAddPaymentClick}
      aria-label="Add payment"
      position="absolute"
      top="1rem"
      iconScale={1.5}
      backgroundColor={theme.palette.secondary.main}
      hoverBackgroundColor={theme.palette.secondary.dark}
      right="10%"
      sx={{
        border: '2px solid #fff',
        '@media (max-width: 600px)': {
          right: '1rem',
        },
        '@media (min-width: 601px)': {
          width: '75px',
          height: '75px',
          minWidth: '75px',
          minHeight: '75px',
        },
        // Approximate alignment with the ACCOUNT tab in BeingsPageSubMenu.
        // ACCOUNT sits at the sum of preceding tab minWidths (clientdetails
        // 266 + newbooking 250 + bookings 208 + newquotation 212 +
        // quotations 154 + newpet 176 + pets 110 = 1376), centred over its
        // own 178px width. Update if those tab widths change. Only applies
        // once the tab row fits without scrolling. ClientAccountCard only
        // renders for clients, so there's no pet-variant offset to account
        // for here.
        '@media (min-width: 1700px)': {
          left: '1465px',
        },
      }}
    />
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <AccountsPageSubMenu
        tabs={ACCOUNT_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <AccountBalanceRow>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <AccountBalanceIcon
            sx={{ color: theme.palette.secondary.main, fontSize: '3rem' }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{ lineHeight: 1.2 }}
              variant="h6"
              fontWeight={700}
              fontSize="2rem"
              color={
                currentBalance === 0
                  ? 'primary.main'
                  : isCredit
                    ? theme.palette.success.main
                    : theme.palette.error.main
              }
            >
              {balanceDisplay}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            ml: 'auto',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            '@media (max-width: 600px)': {
              ml: 0,
              my: 2,
              width: '99%',
              flexDirection: 'column',
              alignItems: 'center',
              '& > button': { width: '100%' },
            },
          }}
        >
          <AccountActionButton
            startIcon={<AddIcon />}
            accentColor={theme.palette.primary.main}
            bgColor={theme.palette.primary.main}
            textColor={theme.palette.grey[0]}
            hoverBgColor={theme.palette.primary.dark}
            hoverTextColor="#fff"
            minWidth="270px"
            onClick={handleAddPaymentClick}
          >
            Add Payment
          </AccountActionButton>
        </Box>
      </AccountBalanceRow>

      <TabPanel value={activeTab} index={0}>
        <CardFooterContainer sx={{ minHeight: 'calc(100vh - 220px)' }}>
          <LedgerList ledger={ledger} />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <CardFooter {...cardFooterProps}>
              {ledger.length > 0 && (
                <Typography sx={footerTextSx}>
                  {ledger.length} Ledger{' '}
                  {ledger.length === 1 ? 'entry' : 'entries'}
                  {(() => {
                    const paymentCount = ledger.filter(
                      (e) => e.type !== 'Invoice' && e.type !== 'Cancellation'
                    ).length;
                    return paymentCount > 0
                      ? ` / ${paymentCount} ${paymentCount === 1 ? 'Payment' : 'Payments'}`
                      : '';
                  })()}
                </Typography>
              )}
            </CardFooter>
            {addPaymentFab}
          </Box>
        </CardFooterContainer>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <CardFooterContainer sx={{ minHeight: 'calc(100vh - 220px)' }}>
          <InvoiceList
            invoices={invoices}
            onDownloadPdf={handleDownloadPdf}
            onPayInvoice={handlePayInvoice}
          />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <CardFooter {...cardFooterProps}>
              {invoices.length > 0 && (
                <Typography sx={footerTextSx}>
                  {invoices.length}{' '}
                  {invoices.length === 1 ? 'Invoice' : 'Invoices'}
                </Typography>
              )}
            </CardFooter>
            {addPaymentFab}
          </Box>
        </CardFooterContainer>
      </TabPanel>

      <AddPaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onSuccess={loadAccountData}
        invoices={invoices}
        clientId={client.id}
        preselectedInvoiceId={preselectedInvoiceId}
      />
    </Box>
  );
};

export default ClientAccountCard;
